import { useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";
import { getTime } from "../helpers";
import {
  restartDesignRequest,
  deleteDesignRequestId,
} from "../../../../services/axios";
import { designAreaLabel } from "../../../../utils/design-areas";

export const DesignHistory = ({
  designs,
  page,
  previousPage,
  nextPage,
}: {
  designs: any[];
  page: number;
  previousPage: () => void;
  nextPage: () => void;
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  let history = useHistory();

  const viewPrintFile = (id: string) => {
    history.push({
      pathname: `/design`,
      search: `?sort=${id}`,
    });
  };

  const restartDesignRequestId = (design: string) => {
    return restartDesignRequest(design)
      .then(() => {
        toast.success("restarted", { duration: 5000 });
      })
      .catch((e) => toast.error(e.message, { duration: 5000 }));
  };

  const deleteAllSelectedDesigns = async () => {
    for (let i = 0; i < selected.length; i++) {
      await deleteDesignRequestId(selected[i]);
      toast.success(selected[i], { duration: 5000 });
    }
    setSelected([]);
    toast.success("deleted", { duration: 5000 });
  };

  const handleSelect = (id: string) => {
    if (selected.includes(id)) {
      const removed = selected.filter((x) => x !== id);
      setSelected(removed);
    } else {
      setSelected([...selected, id]);
    }
  };

  if (!designs) {
    return <></>;
  }

  return (
    <div className="w-full p-8 m-4 border rounded">
      <div className="flex-1 min-w-0 w-full">
        <h4 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
          Proof Request History
        </h4>
        <p className="italic text-xs">Limited to the last month of requests</p>
      </div>
      <div className="w-full flex justify-between items-center gap-4">
        <div className="space-x-4">
          <button
            onClick={() => setSelected(designs.map((x) => x.id))}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            select all
          </button>
          <button
            onClick={() => setSelected([])}
            className="bg-white border px-4 py-2 text-black my-4"
          >
            de-select all
          </button>
        </div>
        <button
          onClick={() => deleteAllSelectedDesigns()}
          className="bg-black px-4 py-2 text-white my-4"
        >
          delete selected
        </button>
      </div>
      <div>
        <table className="table-fixed w-full border-collapse border border-deeppurple text-xs">
          <thead>
            <tr>
              <th className="border border-deeppurple">select</th>
              <th className="border border-deeppurple">design id</th>
              <th className="border border-deeppurple">updated</th>
              <th className="border border-deeppurple">status</th>
              <th className="border border-deeppurple">template name</th>
              <th className="border border-deeppurple">colors</th>
              <th className="border border-deeppurple">design area</th>
              <th className="border border-deeppurple">product</th>
              <th className="border border-deeppurple">design file</th>
              <th className="border border-deeppurple">print File</th>
              <th className="border border-deeppurple">restart</th>
            </tr>
          </thead>
          <tbody>
            {designs
              .filter((x) => x.id)
              .map((design, index) => {
                return (
                  <tr className="p-2 m-2 group" key={index}>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      <div className="w-full h-full flex justify-center items-center">
                        <input
                          id={`${design.id}`}
                          name={`${design.id}`}
                          type="checkbox"
                          checked={selected.includes(`${design.id}`)}
                          className="focus:ring-dustyrose h-4 w-4 text-dustyrose border-gray-300 rounded"
                          onChange={() => handleSelect(`${design.id}`)}
                        />
                      </div>
                    </td>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      {design.id}
                    </td>
                    <td className="p-2 border border-deeppurple group-hover:bg-gray-50">
                      {getTime(design.updated_at)}
                    </td>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      {design.status}
                    </td>
                    <td className="p-2 border border-deeppurple group-hover:bg-gray-50">
                      {design.design_file_name}
                    </td>
                    <td className="p-2 border border-deeppurple group-hover:bg-gray-50">
                      {design.configured_variants
                        ? design.configured_variants
                            .map(
                              (x: { color_name: string; color_hex: string }) =>
                                x.color_name
                            )
                            .join(",")
                        : ""}
                    </td>
                    <td className="p-2 border border-deeppurple group-hover:bg-gray-50">
                      {designAreaLabel(design.configured_design_area)}
                    </td>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      <div className="flex flex-col">
                        <div>{design.product_template_brand}</div>
                        <div>{design.product_template_model}</div>
                        <div>{design.product_template_sku}</div>
                      </div>
                    </td>
                    <td className="p-2 border border-deeppurple group-hover:bg-gray-50">
                      <a
                        href={design.design_file_url}
                        download
                        className="underline pt-2 cursor-pointer"
                      >
                        Download
                      </a>
                    </td>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      {design.status === "complete" ? (
                        <div></div>
                      ) : (
                        <p>waiting for files</p>
                      )}
                      <button
                        className="underline pt-2"
                        onClick={() => viewPrintFile(design.id)}
                      >
                        View Progress
                      </button>
                    </td>
                    <td className="p-2 border border-deeppurple truncate group-hover:bg-gray-50">
                      <button
                        className="underline w-full text-center"
                        onClick={() => restartDesignRequestId(design.id)}
                      >
                        Restart
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
      <nav
        className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 mt-6"
        aria-label="Pagination"
      >
        <div className="hidden sm:block">
          <p className="text-sm text-gray-700">
            Showing page <span className="font-medium">{`${page}`}</span>
          </p>
        </div>
        <div className="flex flex-1 justify-between sm:justify-end">
          <button
            onClick={() => previousPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Previous
          </button>
          <button
            onClick={() => nextPage()}
            className="relative ml-3 inline-flex items-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline-offset-0"
          >
            Next
          </button>
        </div>
      </nav>
    </div>
  );
};
